import { useQuery } from '@tanstack/react-query'
import axios, { AxiosHeaders, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import type { UseQueryOptionsWithoutQueryKey } from '../../react-query-config'
import type { ResponseApiError } from '../../models/axios-error'
import { getMyGameLanguage } from 'core/utils/i18n'
import { mapWeeklyGmvEvolution, type WeeklyGmvEvolution, type WeeklyGmvEvolutionResponse } from './weekly-gmv'
import type { Channel } from './home.model'

export interface TeamLastFourWeeksPerformanceRequest {
  channel: Channel
  storeId: number
  teamId: number | null
}

export interface TopModel {
  modelId: string
  name: string | null
  gmv: number | null
  quantity: number | null
  pictureUrl: string | null
}

export interface TeamLastFourWeeksPerformance {
  topGmvModel: TopModel | null
  topQuantityModel: TopModel | null
  weeklyGmv: WeeklyGmvEvolution | null
}

interface TopModelResponse {
  model_id?: string
  name?: string
  gmv?: number | null
  quantity?: number | null
  picture_url?: string | null
}

interface TeamLastFourWeeksPerformanceResponse {
  top_models?: {
    top_gmv_model?: TopModelResponse | null
    top_quantity_model?: TopModelResponse | null
  }
  weekly_gmv?: WeeklyGmvEvolutionResponse | null
}

const QUERY_KEY_TEAM_LAST_FOUR_WEEKS_PERFORMANCE = 'get-team-last-four-weeks-performance'

export const mapTopModel = (topModel?: TopModelResponse | null): TopModel | null => {
  if (!topModel?.model_id) return null

  return {
    modelId: topModel.model_id,
    name: topModel.name ?? null,
    gmv: topModel.gmv ?? null,
    quantity: topModel.quantity ?? null,
    pictureUrl: topModel.picture_url ?? null
  }
}

const toTeamLastFourWeeksPerformance = (
  response: TeamLastFourWeeksPerformanceResponse
): TeamLastFourWeeksPerformance => ({
  topGmvModel: mapTopModel(response.top_models?.top_gmv_model),
  topQuantityModel: mapTopModel(response.top_models?.top_quantity_model),
  weeklyGmv: mapWeeklyGmvEvolution(response.weekly_gmv)
})

const getTeamLastFourWeeksPerformance = async (
  config: InternalAxiosRequestConfig
): Promise<TeamLastFourWeeksPerformance> => {
  const axiosPromise: AxiosResponse<TeamLastFourWeeksPerformanceResponse> = await axios(config)
  return toTeamLastFourWeeksPerformance(axiosPromise.data)
}

export const useApiGetTeamLastFourWeeksPerformance = (
  params: TeamLastFourWeeksPerformanceRequest,
  enabled: boolean,
  options?: Omit<UseQueryOptionsWithoutQueryKey<TeamLastFourWeeksPerformance, ResponseApiError>, 'queryFn' | 'enabled'>
) => {
  const url = '/team-last-four-weeks-performance'
  const language = getMyGameLanguage()
  const headers = new AxiosHeaders()
  headers.set('Accept-Language', language)
  const config: InternalAxiosRequestConfig = { method: 'get', url, params, headers }

  return useQuery<TeamLastFourWeeksPerformance, ResponseApiError>({
    ...options,
    queryKey: [QUERY_KEY_TEAM_LAST_FOUR_WEEKS_PERFORMANCE, params, language],
    queryFn: () => getTeamLastFourWeeksPerformance(config),
    enabled
  })
}
